import { HocuspocusProvider, WebSocketStatus } from '@hocuspocus/provider';
import { useEffect, useRef, useState } from 'react';
import { IndexeddbPersistence } from 'y-indexeddb';
import * as Y from 'yjs';

import { useLocalStorage } from '../../hooks/useLocalStorage.hook';

export interface YjsProviderHookConfiguration {
    url: string;
    name: string;
    token: () => string;
}

export interface YJSProviderHookResult {
    provider: HocuspocusProvider|null;
    connectionStatus: WebSocketStatus;
    isConnected: boolean;
    wasConnected: boolean;
    isSynced: boolean;
    wasSynced: boolean;
    isSaved: boolean;
    isChanged: boolean;
    lastSaveMeta: LastSaveMeta|null;
}

export interface LastSaveMeta {
    timestamp: number|null;
    changedBy: {
        identityId: string;
        name: string;
        email: string;
    }|null;
}

export const useYjsProvider = (config: YjsProviderHookConfiguration): YJSProviderHookResult => {
    const providerRef = useRef<HocuspocusProvider|null>(null);
    const [ wasConnected, setWasConnected ] = useState(false);
    const [ wasSynced, setWasSynced ] = useState(false);
    const [ isConnected, setIsConnected ] = useState(false);
    const [ isSynced, setIsSynced ] = useState(false);
    const [ connectionStatus, setConnectionStatus ] = useState<WebSocketStatus>(WebSocketStatus.Disconnected);

    const [ isSaved, setIsSaved ] = useState<boolean>(false);
    const [ isChanged, setIsChanged ] = useLocalStorage<boolean>('aic-changed-' + config.name, false);
    const [ lastSaveMeta, setLastSaveMeta ] = useState<LastSaveMeta|null>(null);

    useEffect(() => {
        if (!config.name || !config.url) {
            return;
        }

        const token = config.token();
        console.log('Creating provider', token === '');

        const yDoc = new Y.Doc();

        // Create local persistence layer
        new IndexeddbPersistence('aic-' + config.name, yDoc);

        // Create a new provider
        const provider = new HocuspocusProvider({
            ...config,
            document: yDoc,
            onAwarenessChange() {},
            onConnect() {
                console.log('Connected');
                setIsConnected(true);
                setWasConnected(true);
            },
            onAuthenticated() {
                console.log('Authenticated');
            },
            onDisconnect() {
                console.log('Disconnected');
                setIsConnected(false);
                setIsSynced(false);
            },
            onSynced(event) {
                console.log('Synced', event);
                setIsSynced(event.state);

                if (event.state === true) {
                    setWasSynced(true);
                }
            },
            onStatus(status) {
                setConnectionStatus(status.status);
            },
            onStateless(data) {
                const [ event ] = data.payload.split(':', 2);
                const jsonData = data.payload.substring(event.length + 1);
                
                let eventData: unknown;

                try {
                    if (jsonData !== '') {
                        eventData = JSON.parse(jsonData);
                    }
                } catch (e) {
                    console.error('Error parsing event data', e);
                }

                if (event === 'saved') {
                    setIsSaved(true);
                    setIsChanged(false);
                    setLastSaveMeta(eventData as LastSaveMeta);
                }
            }
        });

        providerRef.current = provider;
        provider.connect();

        provider.document.on('updateV2', (_a, _b, _c, transaction) => {
            if (transaction.local) {
                setIsChanged(true);
                setIsSaved(false);
            }
        });

        return () => {
            console.log('Destroying provider');
            provider.destroy();
            providerRef.current = null;
        };
    }, [ providerRef, config.url, config.name ]);

    // useNavBlockerSetter('documentChanges', !isSaved);

    return {
        provider: providerRef.current,
        wasConnected,
        isConnected,
        wasSynced,
        isSynced,
        connectionStatus,
        isSaved,
        isChanged,
        lastSaveMeta,
    };
};
